import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiAdapter from '../lib/api-adapter';

interface User {
  id: string;
  username: string;
  email: string;
  nickname: string;
  avatar: string;
  role: string;
  bio?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

interface LoginCredentials {
  account: string;
  password: string;
  remember?: boolean;
}

interface RegisterData {
  username: string;
  email: string;
  emailCode: string;
  qq?: string;
  phone?: string;
  password: string;
  school: string;
  nickname?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      clearError: () => set({ error: null }),

      login: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          // 使用API适配器进行登录：传 username 字段（值为邮箱或用户名），
          // 后端 /auth/login 会按 email 或 username 匹配（email 字段受邮箱格式校验，不能直接传）
          const response = await apiAdapter.auth.login({ 
            username: credentials.account, 
            password: credentials.password 
          });

          if (response.success && response.data) {
            const { token, user } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            
            set({ 
              user: user,
              token: token,
              isAuthenticated: true,
              isLoading: false 
            });
          } else {
            throw new Error(response.message || '登录失败');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '登录失败';
          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          // 使用API适配器进行注册
          const response = await apiAdapter.auth.register({
            username: data.username,
            email: data.email,
            password: data.password,
            nickname: data.nickname
          });

          if (response.success && response.data) {
            const { token, user } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            
            set({ 
              user: user,
              token: token,
              isAuthenticated: true,
              isLoading: false 
            });
          } else {
            throw new Error(response.message || '注册失败');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '注册失败';
          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await apiAdapter.auth.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          set({ 
            user: null,
            token: null,
            isAuthenticated: false,
            error: null 
          });
        }
      },

      updateProfile: async (data) => {
        const { user } = get();
        if (!user) return;

        set({ isLoading: true, error: null });
        try {
          const response = await apiAdapter.auth.updateProfile(data);
          
          if (response.success && response.data) {
            const updatedUser = response.data.user;
            localStorage.setItem('user', JSON.stringify(updatedUser));
            set({ 
              user: updatedUser,
              isLoading: false 
            });
          } else {
            throw new Error(response.message || '更新失败');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '更新失败';
          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      checkAuth: async () => {
        const { token } = get();
        
        if (!token) {
          set({ user: null, isAuthenticated: false, isLoading: false });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await apiAdapter.auth.getProfile();

          if (response.success && response.data) {
            const user = response.data.user;
            localStorage.setItem('user', JSON.stringify(user));
            set({ 
              user: user,
              isAuthenticated: true,
              isLoading: false 
            });
          } else {
            // 后端明确返回失败（如 token 失效）：清除登录态
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            set({ 
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false 
            });
          }
        } catch (error: any) {
          // 网络错误（后端临时不可达）不应清除登录态，否则会导致"已登录却显示未登录"
          const status = error?.status ?? error?.response?.status;
          if (status === 401 || status === 403) {
            console.warn('登录态已失效:', error?.message || error);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            set({ 
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false 
            });
          } else {
            console.warn('认证检查网络错误，保留登录态:', error?.message || error);
            set({ isLoading: false });
          }
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated 
      })
    }
  )
);

// 初始化时检查认证状态
if (typeof window !== 'undefined') {
  const token = localStorage.getItem('token');
  if (token) {
    useAuthStore.getState().checkAuth();
  }
}